import type {
  ArticleDto,
  InPriceValuesDto,
  OutPriceConfigDto,
  OutPriceValueDto,
  SeasonDto,
  SkuSeasonSupplierDto,
} from '@/ApiClient.ts';
import { MarkupMarginType } from '@/ApiClient.ts';
import { useActiveSeason } from '@/store/active-season.ts';
import { useDestinations } from '@/store/destinations';
import { useSelectedDestination } from '@/store/selected-destination';
import { useMarkupTargets } from '@/store/markup-targets';
import { useOutPriceConfigs } from '@/store/out-price-configs.ts';

export const findOutPriceValuesForArticleSeason = (
  outPriceConfig?: OutPriceConfigDto,
  seasonality?: { outPriceValues: OutPriceValueDto[] },
  hierarchyNodeId?: number
): OutPriceValueDto[] => {
  const outPrices = outPriceConfig?.outPrices.map((op) => {
    const outPriceValue = seasonality?.outPriceValues?.find((opv) => {
      return opv.outPriceId === op.id;
    });

    // We need to fallback to outPrice's currencyId if outPriceValue has no currencyId set
    const currencyId = outPriceValue?.currencyId ?? op.currencyId;

    if (outPriceValue) {
      // To break reference, so it can be modified without save and still keep original
      return { ...outPriceValue, currencyId };
    }

    return {
      id: -1,
      outPriceId: op.id,
      outPriceKey: op.key,
      destinationId: outPriceConfig.destinationId,
      markupMarginTarget: useMarkupTargets().getForHierarchy(op.id, hierarchyNodeId),
      markupMarginType: op.preferMarkup ? MarkupMarginType.Markup : MarkupMarginType.Margin,
      amount: undefined,
      currencyId: op.currencyId,
      outPriceName: op.name,
      linked: !!op.calculationTemplate,
    };
  });

  return outPrices ?? [];
};

export const getArticleSeason = (article: ArticleDto, season?: SeasonDto | null) => {
  if (!season) {
    return article.seasonalities[0];
  }

  return article.seasonalities.find((as) => as.id === season?.id);
};

const getSkuSeason = (article: ArticleDto, season?: SeasonDto | null) => {
  const firstSku = article.skus?.[0];
  if (!firstSku) return undefined;
  if (!season) {
    return firstSku.seasonalities?.[0];
  }
  return firstSku.seasonalities?.find((s) => s.id === season.id);
};

export const getFirstOutPriceValuesForArticle = (article: ArticleDto, season?: SeasonDto) => {
  const { activeSeason } = useActiveSeason();
  const skuSeason = getSkuSeason(article, season ?? activeSeason);
  const outPriceConfig = useOutPriceConfigs().getBySeasonId(skuSeason?.id);
  const outPrice = findOutPriceValuesForArticleSeason(outPriceConfig, skuSeason)?.[0];

  return outPrice;
};

/** Read-only summaries show the selected destination's prices — the market the user is working in. */
const getSelectedDestinationInPrice = (supplier: SkuSeasonSupplierDto | undefined): InPriceValuesDto | undefined =>
  supplier?.inPriceValues.find((v) => v.destinationId === useSelectedDestination().selectedDestination?.id);

export const getFirstInPriceForArticle = (
  article: ArticleDto,
  season?: SeasonDto | null
): InPriceValuesDto | undefined => {
  const { activeSeason } = useActiveSeason();
  const skuSeason = getSkuSeason(article, season ?? activeSeason);

  const firstSupplier = skuSeason?.suppliers?.[0];

  return getSelectedDestinationInPrice(firstSupplier);
};

export const getInPriceValuesForSupplierSeason = (
  article: ArticleDto,
  supplierId: number | null,
  season?: SeasonDto | null | undefined
): InPriceValuesDto | undefined => {
  const { activeSeason } = useActiveSeason();
  const skuSeason = getSkuSeason(article, season ?? activeSeason);
  const supplier = skuSeason?.suppliers.find((s) => s.supplierId === supplierId);

  return getSelectedDestinationInPrice(supplier);
};

export const getTrendPrefix = (diff: number, inverted?: boolean) =>
  diff > 0.1 ? (inverted ? '+' : '-') : diff < -0.1 ? (inverted ? '-' : '+') : '';
